import React from 'react';
import { Helmet } from 'react-helmet-async';

const defaultSEO = {
  title: 'VYTA | AI-Powered Personal Training & Nutrition Plans',
  description: 'Get 100% personalized workout programs and meal plans tailored to YOUR body, goals, and lifestyle. AI-powered fitness coaching with science-backed training.',
  image: 'https://vyta.com/og-image.png',
  url: 'https://vyta.com',
};

export default function SEO({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  keywords,
  noindex = false 
}) {
  const seo = {
    title: title ? `${title} | VYTA` : defaultSEO.title,
    description: description || defaultSEO.description,
    image: image || defaultSEO.image,
    url: url || defaultSEO.url,
  };

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={type} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seo.url} />
    </Helmet>
  );
}

// Pre-defined SEO configs for each page
export const pageSEO = {
  landing: {
    title: null, // Uses default
    description: 'Get 100% personalized workout programs and meal plans tailored to YOUR body, goals, and lifestyle. Start your 7-day free trial with AI-powered fitness coaching.',
    keywords: 'AI fitness app, personalized workout, custom meal plan, AI personal trainer, fitness coaching',
    url: 'https://vyta.com/',
  },
  about: {
    title: 'About Us',
    description: 'Learn about VYTA\'s mission to make elite, personalized fitness coaching accessible to everyone through AI technology. Science-backed training for all fitness levels.',
    keywords: 'about VYTA, fitness company, AI fitness technology, personalized training company',
    url: 'https://vyta.com/about',
  },
  science: {
    title: 'The Science Behind VYTA',
    description: 'Discover the research and science behind VYTA\'s AI-powered training methodology. Evidence-based fitness programming backed by sports science.',
    keywords: 'fitness science, exercise research, sports science, evidence-based training, workout methodology',
    url: 'https://vyta.com/science',
  },
  eventTraining: {
    title: 'Event-Specific Training - Marathon, Hyrox, CrossFit & More',
    description: 'Train for your next event with AI-generated periodized programs. VYTA creates specialized training plans for marathons, Hyrox, CrossFit competitions, triathlons, and more.',
    keywords: 'marathon training plan, Hyrox training, CrossFit program, triathlon training, race preparation, event training, periodization',
    url: 'https://vyta.com/event-training',
  },
  register: {
    title: 'Start Your Free Trial',
    description: 'Sign up for VYTA and get your personalized AI-generated workout and meal plan. 7-day free trial, cancel anytime.',
    keywords: 'fitness app sign up, free trial workout app, AI trainer signup',
    url: 'https://vyta.com/register',
  },
  login: {
    title: 'Login',
    description: 'Log in to your VYTA account to access your personalized training and nutrition programs.',
    url: 'https://vyta.com/login',
    noindex: true,
  },
};
